import coreAPI from "@/datasources/coreAPI";

// Types matching core-api SamSession entity
export interface SamSessionDto {
  id: number;
  userId: number;
  enterpriseId: number;
  flowiseChatId: string;
  title: string | null;
  metadata: Record<string, unknown> | null;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface SessionListResponse {
  sessions: SamSessionDto[];
  nextCursor: number | null;
  hasMore: boolean;
}

export interface CreateSessionPayload {
  flowiseChatId: string;
  title?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateSessionPayload {
  title?: string;
  metadata?: Record<string, unknown>;
}

// Flowise message format (from core-api proxy)
export interface FlowiseMessage {
  id: string;
  role: "userMessage" | "apiMessage";
  content: string;
  chatId?: string;
  chatflowid?: string;
  sourceDocuments?: Record<string, unknown>[];
  usedTools?: Record<string, unknown>[];
  fileAnnotations?: Record<string, unknown>[];
  artifacts?: Record<string, unknown>[];
  followUpPrompts?: string[];
  agentReasoning?: Record<string, unknown>[];
  action?: Record<string, unknown>;
  rating?: string;
  createdDate: string;
}

export interface FlowiseMessagesResponse {
  messages: FlowiseMessage[];
}

/**
 * SAM Session API client
 * Interacts with core-api endpoints for session persistence
 */
export const samSessionApi = {
  /**
   * List all sessions for a user in an enterprise
   * Supports cursor-based pagination
   */
  async listSessions(
    enterpriseId: number,
    userId: number,
    params?: { cursor?: number; limit?: number; includeDeleted?: boolean }
  ): Promise<SessionListResponse> {
    const { data } = await coreAPI.get(
      `/enterprises/${enterpriseId}/users/${userId}/sam-sessions`,
      { params }
    );
    return data;
  },

  /**
   * Get a single session by ID
   */
  async getSession(
    enterpriseId: number,
    userId: number,
    sessionId: number
  ): Promise<SamSessionDto> {
    const { data } = await coreAPI.get(
      `/enterprises/${enterpriseId}/users/${userId}/sam-sessions/${sessionId}`
    );
    return data;
  },

  /**
   * Create a new session
   * Links a Flowise chatId to a persistent session
   */
  async createSession(
    enterpriseId: number,
    userId: number,
    payload: CreateSessionPayload
  ): Promise<SamSessionDto> {
    const { data } = await coreAPI.post(
      `/enterprises/${enterpriseId}/users/${userId}/sam-sessions`,
      payload
    );
    return data;
  },

  /**
   * Update session title or metadata
   */
  async updateSession(
    enterpriseId: number,
    userId: number,
    sessionId: number,
    payload: UpdateSessionPayload
  ): Promise<SamSessionDto> {
    const { data } = await coreAPI.patch(
      `/enterprises/${enterpriseId}/users/${userId}/sam-sessions/${sessionId}`,
      payload
    );
    return data;
  },

  /**
   * Soft delete a session
   */
  async deleteSession(
    enterpriseId: number,
    userId: number,
    sessionId: number
  ): Promise<void> {
    await coreAPI.delete(
      `/enterprises/${enterpriseId}/users/${userId}/sam-sessions/${sessionId}`
    );
  },

  /**
   * Get messages for a session from Flowise (via core-api proxy)
   */
  async getMessages(
    enterpriseId: number,
    userId: number,
    sessionId: number,
    params?: { order?: "ASC" | "DESC" }
  ): Promise<FlowiseMessagesResponse> {
    const { data } = await coreAPI.get(
      `/enterprises/${enterpriseId}/users/${userId}/sam-sessions/${sessionId}/messages`,
      { params }
    );
    return data;
  },
};

export default samSessionApi;
